<template>
  <div class="pa-wins">
    <div v-if="loading" class="pa-wins__loading mainColor--text">Loading…</div>

    <!-- Connected to Search Console but the monitoring consent is off: the White Glove tab would
         render an empty report with no prompt, so gate it (design pa-app.jsx gscWarn) -->
    <div v-else-if="needsSetup" class="pa-trackcard pa-wins__gate">
      <i class="material-icons" aria-hidden="true">insights</i>
      <p>
        <b>Turn on tracking to see your Wins.</b>
        <template v-if="canEnableOneClick">
          One click; tracking uses the Search Console connection you already made. Wins and your
          monthly reports then show keyword movement from day one.
        </template>
        <template v-else>
          Accept the data access terms and turn tracking on under Settings (one minute).
        </template>
      </p>
      <button
        v-if="canEnableOneClick"
        type="button"
        class="pa-btn pa-btn--orange"
        :disabled="busy"
        @click="enableTracking"
      >
        {{ busy ? 'Turning on…' : 'Turn on tracking' }}
      </button>
      <router-link v-else class="pa-btn pa-btn--orange" :to="settingsGscRoute">Open Settings</router-link>
    </div>

    <!-- Not connected / expired / tracking on: the White Glove Wins tab (own gate + expired banner) -->
    <wins-white-glove v-else :key="loadKey" />
  </div>
</template>

<script>
import WinsWhiteGlove from '@/components/SitesSingleView/WhiteGlove/Wins.vue'
import {
  POP_AUTHORITY_SETTINGS_GSC_ROUTE,
  popAuthorityCanEnableTrackingOneClick,
  popAuthorityTrackingNeedsSetup,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

/**
 * POP Authority Wins tab (Phase 9): the White Glove Wins tab behind a tracking gate. Search
 * Console tracking is shared machinery (GscConnection.monitoringEnabled is the Wins trigger);
 * this wrapper only adds the "turn on tracking" step the POP Authority setup lets clients skip.
 * Never sends `termsAccepted` on the client's behalf: without the terms the button goes to Settings.
 */
export default {
  name: 'WinsPopAuthority',
  components: { WinsWhiteGlove },
  inject: {
    refreshWhiteGloveOrders: { default: null },
  },
  data () {
    return {
      loading: true,
      busy: false,
      connection: null,
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    /** Same reload key as the White Glove tab: a restored sub-account relation reloads the connection. */
    subAccountRelationId () {
      const r = this.$store.state.selectedSubAccountRelation
      return r && typeof r === 'object' && r.id != null ? r.id : null
    },
    loadKey () {
      return `${this.siteId}:${this.subAccountRelationId}`
    },
    needsSetup () {
      return popAuthorityTrackingNeedsSetup(this.connection)
    },
    canEnableOneClick () {
      return popAuthorityCanEnableTrackingOneClick(this.connection)
    },
    settingsGscRoute () {
      return { ...POP_AUTHORITY_SETTINGS_GSC_ROUTE, params: { siteId: String(this.$route.params.siteId) } }
    },
  },
  watch: {
    loadKey: {
      handler () {
        this.loadConnection()
      },
      immediate: true,
    },
  },
  methods: {
    loadConnection () {
      if (!this.siteId) return
      this.loading = true
      this.$store.dispatch('getGscConnection', this.siteId)
        .then((response) => {
          const data = response && response.data
          this.connection = (data && data.status === 'SUCCESS' && data.connection) || null
        })
        .catch(() => {
          this.connection = null
        })
        .finally(() => {
          this.loading = false
        })
    },
    /** PUT .../gsc/settings { monitoringEnabled: 1 }: seeds the first pull and tells the staff (backend). */
    enableTracking () {
      if (this.busy || !this.siteId) return
      this.busy = true
      this.$store.dispatch('updateGscSettings', { siteId: this.siteId, body: { monitoringEnabled: 1 } })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            this.connection = data.connection || { ...this.connection, monitoringEnabled: 1 }
            showPopAuthorityToast(this, 'Campaign tracking turned on')
            // the Overview card and timeline read the statuses payload
            if (this.refreshWhiteGloveOrders) this.refreshWhiteGloveOrders()
            return
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Could not turn on tracking' })
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Could not turn on tracking'
          this.$notify({ group: 'info', type: 'error', text: msg })
        })
        .finally(() => {
          this.busy = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

.pa-wins {
  &__loading {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__gate {
    margin: 4px 0 22px;
  }
}
</style>
